"use client";

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function daysUntil(msEpoch: number): number {
  return Math.max(0, Math.ceil((msEpoch - Date.now()) / (1000 * 60 * 60 * 24)));
}

function formatDate(msEpoch: number): string {
  return new Date(msEpoch).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ─── Banner variants ──────────────────────────────────────────────────────────

type BannerVariant = "warning" | "danger" | "info";

interface BannerConfig {
  variant: BannerVariant;
  message: string;
  ctaLabel: string;
  ctaHref: string;
}

const VARIANT_STYLES: Record<BannerVariant, string> = {
  warning:
    "bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-950/40 dark:border-amber-700 dark:text-amber-200",
  danger:
    "bg-red-50 border-red-300 text-red-900 dark:bg-red-950/40 dark:border-red-700 dark:text-red-200",
  info: "bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-950/40 dark:border-blue-700 dark:text-blue-200",
};

const CTA_STYLES: Record<BannerVariant, string> = {
  warning:
    "text-amber-800 underline underline-offset-2 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100",
  danger:
    "text-red-800 underline underline-offset-2 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100",
  info: "text-blue-800 underline underline-offset-2 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100",
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SubscriptionBanner
 *
 * Displays a contextual warning strip at the top of premium-gated content
 * when the user's subscription requires attention. Renders nothing when the
 * subscription is in good standing or loading.
 *
 * Scenarios handled:
 * - Trial ending soon (≤ 3 days remaining)
 * - Past-due payment — urges the user to update their payment method
 * - Canceling at period end — countdown to access expiry
 * - In grace period — post-expiry buffer before access is revoked
 */
export function SubscriptionBanner() {
  const {
    isLoading,
    status,
    isTrialing,
    trialEndsAt,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    gracePeriod,
    isAdmin,
  } = useSubscription();

  const config = useMemo<BannerConfig | null>(() => {
    if (isLoading || isAdmin) return null;

    // Grace period — subscription has expired but the buffer window is still open
    if (gracePeriod.isInGracePeriod && gracePeriod.graceEndsAt) {
      const days = daysUntil(gracePeriod.graceEndsAt);
      return {
        variant: "danger",
        message:
          days === 0
            ? "Your subscription has expired. Access will be revoked soon."
            : `Your subscription has expired. You have ${days} day${days === 1 ? "" : "s"} left before access is removed.`,
        ctaLabel: "Renew now",
        ctaHref: "/pricing?source=grace_period_banner",
      };
    }

    // Payment past due — Polar/Stripe is retrying
    if (status === "past_due") {
      return {
        variant: "danger",
        message:
          "Your last payment failed. Please update your payment method to keep access.",
        ctaLabel: "Update payment",
        ctaHref: "/dashboard/settings/billing",
      };
    }

    // Trial ending soon (warn when ≤ 3 days remain)
    if (isTrialing && trialEndsAt) {
      const days = daysUntil(trialEndsAt);
      if (days <= 3) {
        return {
          variant: "warning",
          message:
            days === 0
              ? "Your free trial ends today. Subscribe to keep access."
              : `Your free trial ends in ${days} day${days === 1 ? "" : "s"} (${formatDate(trialEndsAt)}).`,
          ctaLabel: "Subscribe now",
          ctaHref: "/pricing?source=trial_ending_banner",
        };
      }
    }

    // Canceled but still within paid period — show countdown
    if (cancelAtPeriodEnd && currentPeriodEnd && currentPeriodEnd > Date.now()) {
      const days = daysUntil(currentPeriodEnd);
      return {
        variant: "info",
        message:
          days === 0
            ? `Your subscription expires today (${formatDate(currentPeriodEnd)}).`
            : `Your subscription is canceled and expires in ${days} day${days === 1 ? "" : "s"} on ${formatDate(currentPeriodEnd)}.`,
        ctaLabel: "Resume subscription",
        ctaHref: "/dashboard/settings/billing",
      };
    }

    return null;
  }, [
    isLoading,
    isAdmin,
    gracePeriod,
    status,
    isTrialing,
    trialEndsAt,
    cancelAtPeriodEnd,
    currentPeriodEnd,
  ]);

  if (!config) return null;

  return (
    <div
      role="alert"
      className={`w-full border-b px-4 py-2.5 text-sm ${VARIANT_STYLES[config.variant]}`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
        <span>{config.message}</span>
        <Link
          to={config.ctaHref}
          className={`shrink-0 font-medium ${CTA_STYLES[config.variant]}`}
        >
          {config.ctaLabel} &rarr;
        </Link>
      </div>
    </div>
  );
}
